import axios from 'redaxios';

import slideDown from '../../util/slide-down';
import slideUp from '../../util/slide-up';

const STATES = {
  INITIAL: 0,
  LOADING: 1,
  SUCCESS: 2,
  ERROR: 3,
};

const selectors = {
  popdown: '[data-product-add-popdown-wrapper]',
  errors: '[data-add-action-errors]',
  errorBoundary: '[data-error-boundary]',
  errorDisplay: '[data-error-display]',
  quickviewModal: '[data-quickview-modal]',
  cartPageUpsellWrapper: '[data-cart-page-upsell-wrapper]',
  sectionTypeCart: '[data-section-type="cart"]',
  ajaxDisable: '[data-ajax-disable="true"]',
};

const defaultTimes = {
  durationAddButtonDisable: 3500,
  hideErrorTime: 5000,
};

/*
NOTE this is an initial crack at migrating existing product instant add logic
from WebComponents / direct DOM manipulation style to using Alpine. As such, it is
fairly rough and not at all (yet) idiomatic Alpine.
*/

function productAddButtonForm(options = {}) {
  const times = {...defaultTimes, ...options.times};

  return {
    state: STATES.INITIAL,
    error: '',
    isUpsell: false,
    isInCartPage: false,

    // Selectors

    get cartPageUpsellWrapper() {
      return this.$el.closest(selectors.cartPageUpsellWrapper);
    },

    get sectionTypeCart() {
      return this.$el.closest(selectors.sectionTypeCart);
    },

    get form() {
      if (this.isUpsell && this.isInCartPage) {
        // Get Cart form element to construct a proper FormData object that would contain the productAddButtonForm's fields and their values
        return this.$el.closest('form');
      }
      return this.$el.querySelector('form');
    },

    get button() {
      return this.$el.querySelector('button');
    },

    get popdown() {
      return document.querySelector(selectors.popdown);
    },

    get errorDisplay() {
      let errorsEl = this.$el.querySelector(selectors.errors);

      if (!errorsEl) {
        const ancestorErrorBoundary = this.$el.closest(selectors.errorBoundary);

        if (ancestorErrorBoundary) {
          errorsEl = ancestorErrorBoundary.querySelector(selectors.errorDisplay);
        }
      }

      if (!errorsEl) {
        errorsEl = this.$el.querySelector(selectors.errorDisplay);
      }

      return errorsEl;
    },

    get quickViewModal() {
      return this.$el.closest(selectors.quickviewModal);
    },

    // Getters

    get isInitial() {
      return this.state === STATES.INITIAL;
    },

    get isLoading() {
      return this.state === STATES.LOADING;
    },

    get isSuccess() {
      return this.state === STATES.SUCCESS;
    },

    get isError() {
      return this.state === STATES.ERROR;
    },

    get isDisabled() {
      return this.isLoading;
    },

    get isAjaxDisabled() {
      return !!this.$el.closest(selectors.ajaxDisable);
    },

    // Bleh, this kind of sucks but neccesary evil for now
    get isInsideQuickViewModal() {
      return Boolean(this.quickViewModal);
    },

    // Handlers

    handleClickAddButton(e) {
      e.preventDefault();

      this.state = STATES.LOADING;

      const formData = new FormData(this.form);

      this.addToCart(formData).then(this.handleSuccess.bind(this)).catch(this.handleError.bind(this));
    },

    handleSuccess(response) {
      const variant = response.data;

      this.state = STATES.SUCCESS;

      this.updateHeaderTotal();

      // Reset the state back to initial after a duration
      setTimeout(() => {
        this.state = STATES.INITIAL;
      }, times.durationAddButtonDisable);

      // If we're inside of a quick view, close the quick view.
      if (this.isInsideQuickViewModal) {
        this.closeQuickView();
      }

      // If cart is open, reload the cart instead of opening a popdown
      if (window.theme.state.cartOpen || this.isInCartPage || this.isOnCartPage) {
        this.reloadCart();
      } else {
        this.openPopdown(variant);
      }
    },

    handleError(error) {
      if (error?.data) {
        this.state = STATES.ERROR;
        if (error.data.description && typeof error.data.description === 'string') {
          // Standard stockout error
          this.error = error.data.description;
        } else if (error.data.message && typeof error.data.message === 'string') {
          // Standard giftcard validation error
          this.error = error.data.message;
        } else if (error.data.description && typeof error.data.description === 'object') {
          // Error is custom object, print keys and values into message to avoid [Object object]
          this.error = Object.keys(error.data.description)
            .map((key) => {
              return `${key}: ${error.data.description[key]}`;
            })
            .join('<br>');
        } else {
          // Fallback
          this.error = 'Network error: please try again';
        }

        if (this.errorDisplay) {
          const errorsHTML = `<div class="errors">${this.error}</div>`;
          this.errorDisplay.innerHTML = errorsHTML;

          slideDown(this.errorDisplay);
          setTimeout(() => {
            slideUp(this.errorDisplay);
          }, times.hideErrorTime);
        }
      } else {
        throw error;
      }
    },

    // Actions

    addToCart(formData) {
      const url = `${window.theme.routes.cart}/add.js`;
      const formPayload = new URLSearchParams(formData).toString();

      return axios.post(url, formPayload, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
    },

    updateHeaderTotal() {
      return axios
        .get(`${window.theme.routes.cart}.js`)
        .then((response) => {
          document.dispatchEvent(
            new CustomEvent('theme:cart:change', {
              detail: {
                cart: response.data,
              },
              bubbles: true,
            })
          );
        })
        .catch((e) => {
          console.error(e);
        });
    },

    closeQuickView() {
      this.quickViewModal.dispatchEvent(new CustomEvent('theme:quickview:close'));
    },

    reloadCart() {
      document.dispatchEvent(new CustomEvent('theme:cart:reload', {bubbles: true}));
    },

    openPopdown(variant) {
      this.popdown.dispatchEvent(
        new CustomEvent('theme:cart:popdown', {
          detail: {
            variant: variant,
          },
          bubbles: true,
        })
      );
    },

    get isInCartPage() {
      return Boolean(this.sectionTypeCart);
    },

    get isOnCartPage() {
      return Boolean(document.body.querySelector(selectors.sectionTypeCart));
    },

    get isUpsell() {
      return Boolean(this.cartPageUpsellWrapper);
    },

    // Initialization

    init() {
      this.button.addEventListener('click', this.handleClickAddButton.bind(this));
    },
  };
}

export default productAddButtonForm;
